import { ServerSideMetricsDetailByTime } from '@pubstack/common/src/analytics/query'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { Timezone } from '@pubstack/common/src/timezone'
import { DateTime } from 'luxon'
import { FunctionComponent, useState } from 'react'
import { ChartWrapperOptions } from 'react-google-charts'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { TabProp, Tabs } from '~/components/Tabs'
import { WidgetProps } from '~/components/Widget'
import { AnalyticsDefaultChartOptions } from '~/modules/analytics/AnalyticsCharts'
import { TimelineConfiguration, useTimelineChart } from '~/modules/analytics/analyticsTimeline.hooks'
import { serverSideBidCpm, serverSideBidRequests, serverSideBidRpm, serverSideBidTimeouts, serverSideECPM, serverSideImpressionRpm } from '~/modules/analytics/common/formulas'
import { WithClassName } from '~/types/utils'
import { ANALYTICS_TOOLTIPS } from '~/utils/constants'
import { displayWithCurrency } from '~/utils/string'
import { AnalyticsChartWidget } from '../../AnalyticsChartWidget'

const useConf = (type: BidderCPMDataType, currencySymbol: CurrencySymbol): TimelineConfiguration<ServerSideMetricsDetailByTime> => {
  const chartOptions: ChartWrapperOptions['options'] = {
    ...AnalyticsDefaultChartOptions,
    annotations: { ...AnalyticsDefaultChartOptions.annotations, style: 'point' },
    crosshair: { ...AnalyticsDefaultChartOptions.crosshair, trigger: 'both' },
    vAxis: {
      ...AnalyticsDefaultChartOptions.vAxis,
      gridlines: { count: -1 },
      minorGridlines: { color: 'none' },
      format: displayWithCurrency('#.##', currencySymbol),
      minValue: 0,
    },
  }
  return type === 'CPM'
    ? {
        getChartOptions: () => chartOptions,
        legendConfig: ['serverSideECPM', 'serverSideBidCpm'],
        tooltipConfig: ['serverSideECPM', 'serverSideBidCpm'],
        dataConfig: [
          {
            name: 'serverSideECPM',
            isComputable: ({ data }) => serverSideECPM.isComputable(data),
            getFormula: () => serverSideECPM,
            legendTooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_BIDDER_DETAILS_IMPRESSION_ECPM,
            iconName: 'data_line',
            withValue: true,
          },
          // {
          //   name: 'bidWinCpm',
          //   isComputable: ({ data }) => bidWinCpm.isComputable(data),
          //   getFormula: () => bidWinCpm,
          //   legendTooltip: ANALYTICS_TOOLTIPS.BIDDER_DETAILS_WIN_CPM,
          //   iconName: 'data_line',
          //   withValue: true,
          // },
          {
            name: 'serverSideBidCpm',
            isComputable: ({ data }) => serverSideBidCpm.isComputable(data),
            getFormula: () => serverSideBidCpm,
            legendTooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_BIDDER_DETAILS_BID_CPM,
            iconName: 'data_line',
            withValue: true,
          },
        ],
      }
    : {
        getChartOptions: () => chartOptions,
        legendConfig: ['serverSideImpressionRpm', 'serverSideBidRpm'],
        tooltipConfig: ['serverSideImpressionRpm', 'serverSideBidRpm'],
        dataConfig: [
          {
            name: 'serverSideImpressionRpm',
            isComputable: ({ data }) => serverSideImpressionRpm.isComputable(data),
            getFormula: () => serverSideImpressionRpm,
            legendTooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_IMPRESSION_RPM,
            iconName: 'data_line',
            withValue: true,
          },
          // {
          //   name: 'bidWinRpm',
          //   isComputable: ({ data }) => bidWinRpm.isComputable(data),
          //   getFormula: () => bidWinRpm,
          //   legendTooltip: ANALYTICS_TOOLTIPS.BIDDER_DETAILS_WIN_RPM,
          //   iconName: 'data_line',
          //   withValue: true,
          // },
          {
            name: 'serverSideBidRpm',
            isComputable: ({ data }) => serverSideBidRpm.isComputable(data),
            getFormula: () => serverSideBidRpm,
            legendTooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_BID_RPM,
            iconName: 'data_line',
            withValue: true,
          },
        ],
      }
}

export type BidderCPMDataType = 'CPM' | 'RPM'
const dataTypes: { type: BidderCPMDataType; label: string; trackingLabel: TrackingLabel }[] = [
  { type: 'CPM', label: 'CPM', trackingLabel: 'ana-server-side-detail-cpm-timeline-cpm-tab-apply' },
  { type: 'RPM', label: 'RPM', trackingLabel: 'ana-server-side-detail-cpm-timeline-rpm-tab-apply' },
]

type ServerSideWidgetCPMProps = WithClassName &
  Omit<WidgetProps, 'title' | 'icon' | 'info' | 'refreshTrackingLabel'> & {
    data: ServerSideMetricsDetailByTime
    currentEpoch: DateTime
    currencySymbol: CurrencySymbol
    onTabChange?: (type: BidderCPMDataType) => void
    timezone: Timezone
  }
export const ServerSideWidgetCPM: FunctionComponent<ServerSideWidgetCPMProps> = ({ onTabChange, data, currencySymbol, timezone, currentEpoch, ...props }) => {
  const [currentTab, setCurrentTab] = useState(0)
  const tabs: TabProp[] = dataTypes.map((type, index) => ({ label: type.label, active: currentTab === index, trackingLabel: type.trackingLabel }))
  const allComputable = data && serverSideBidRequests.isComputable(data) && serverSideBidTimeouts.isComputable(data)
  const showTabs = !props.empty && allComputable
  const onTabClick = (tab: TabProp) => {
    const index = tabs.indexOf(tab)
    setCurrentTab(index)
    onTabChange && onTabChange(dataTypes[index].type)
  }

  const type = dataTypes[currentTab].type
  const currentConfig = useConf(type, currencySymbol)

  const { chart, legends } = useTimelineChart({ currentConfig, data, currencySymbol, currentEpoch, timezone })
  return (
    <AnalyticsChartWidget
      {...props}
      icon={'bidder'}
      title={`Server ${type} Breakdown`}
      info={showTabs && <Tabs tabs={tabs} fluid={false} onClick={onTabClick} />}
      smallChart={true}
      chart={chart}
      legends={legends}
      refreshTrackingLabel={'ana-server-side-detail-cpm-timeline-refresh'}
    />
  )
}
